import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AppShell } from './components/layout/AppShell';
import { ProtectedRoute } from './components/layout/ProtectedRoute';

// Pages
import LoginPage from './pages/auth/LoginPage';
import SsoCallbackPage from './pages/auth/SsoCallbackPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import BaselinesPage from './pages/firmware/BaselinesPage';
import UploadBaselinePage from './pages/firmware/UploadBaselinePage';
import BaselineDetailPage from './pages/firmware/BaselineDetailPage';
import RolloutsPage from './pages/rollouts/RolloutsPage';
import RolloutDetailPage from './pages/rollouts/RolloutDetailPage';
import CurrentBaselineBreakdownPage from './pages/rollouts/CurrentBaselineBreakdownPage';
import PreviousActiveBreakdownPage from './pages/rollouts/PreviousActiveBreakdownPage';
import DeprecatedFirmwareBreakdownPage from './pages/rollouts/DeprecatedFirmwareBreakdownPage';
import DeviceRegistryPage from './pages/devices/DeviceRegistryPage';
import DeviceDetailPage from './pages/devices/DeviceDetailPage';
import TelemetryPage from './pages/telemetry/TelemetryPage';
import ExportPage from './pages/telemetry/ExportPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import SendNotificationPage from './pages/admin/SendNotificationPage';
import DeviceCatalogPage from './pages/admin/DeviceCatalogPage';
import SegmentsPage from './pages/admin/SegmentsPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import ClientActivitySessionsPage from './pages/activitySessions/ClientActivitySessionsPage';
import ClientLogDetailPage from './pages/activitySessions/ClientLogDetailPage';
import DeviceActivitySessionsPage from './pages/activitySessions/DeviceActivitySessionsPage';
import SessionAnalyticsPage from './pages/activitySessions/SessionAnalyticsPage';
import DeviceLogDetailPage from './pages/activitySessions/DeviceLogDetailPage';
import UnusualActivityPage from './pages/admin/UnusualActivityPage';
import SystemConfigPage from './pages/admin/SystemConfigPage';
import SystemHealthPage from './pages/admin/SystemHealthPage';
import LogTriagePage from './pages/inc2/LogTriagePage';

function ProtectedLayout({ children, allowedRoles }) {
  return (
    <ProtectedRoute allowedRoles={allowedRoles}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <NotificationProvider>
            <Routes>
              {/* Public */}
              <Route path="/login" element={<LoginPage />} />
              {/* Opened from the password reset e-mail; the user has no session. */}
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              {/* Hosted UI SSO return leg. Must stay public — the session does
                  not exist yet when this route renders. Path matches
                  REACT_APP_REDIRECT_SIGN_IN and the Cognito app client's
                  configured callback URL. */}
              <Route path="/callback" element={<SsoCallbackPage />} />

            {/* Dashboard — all web roles, including read-only (Nurse/Patient) */}
            <Route
              path="/"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager', 'SupportEngineer', 'Nurse', 'Patient']}>
                  <DashboardPage />
                </ProtectedLayout>
              }
            />

              {/* Firmware — Admin + RM */}
              <Route
                path="/firmware"
                element={
                  <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager']}>
                    <BaselinesPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/firmware/upload"
                element={
                  <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager']}>
                    <UploadBaselinePage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/firmware/:id"
                element={
                  <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager']}>
                    <BaselineDetailPage />
                  </ProtectedLayout>
                }
              />

            {/* Rollouts — all web roles, including read-only (Nurse/Patient) */}
            <Route
              path="/rollouts"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager', 'SupportEngineer', 'Nurse', 'Patient']}>
                  <RolloutsPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/rollouts/:id"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager', 'SupportEngineer', 'Nurse', 'Patient']}>
                  <RolloutDetailPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/rollouts/current-baseline"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager', 'SupportEngineer', 'Nurse', 'Patient']}>
                  <CurrentBaselineBreakdownPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/rollouts/previous-active"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager', 'SupportEngineer', 'Nurse', 'Patient']}>
                  <PreviousActiveBreakdownPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/rollouts/deprecated/:firmwareKey"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager', 'SupportEngineer', 'Nurse', 'Patient']}>
                  <DeprecatedFirmwareBreakdownPage />
                </ProtectedLayout>
              }
            />

            {/* Devices — Admin + Support + ReleaseManager + read-only (Nurse/Patient) */}
            <Route
              path="/devices"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'SupportEngineer', 'ReleaseManager', 'Nurse', 'Patient']}>
                  <DeviceRegistryPage />
                </ProtectedLayout>
              }
            />
            <Route
              path="/devices/:serial"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'SupportEngineer', 'ReleaseManager', 'Nurse', 'Patient']}>
                  <DeviceDetailPage />
                </ProtectedLayout>
              }
            />

            {/* Telemetry — all web roles, including read-only (Nurse/Patient) */}
            <Route
              path="/telemetry"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager', 'SupportEngineer', 'Nurse', 'Patient']}>
                  <TelemetryPage />
                </ProtectedLayout>
              }
            />
            {/* Export/download page — Nurse/Patient can view, but downloads are refused in-page (003 Role Gate) */}
            <Route
              path="/telemetry/export"
              element={
                <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager', 'SupportEngineer', 'Nurse', 'Patient']}>
                  <ExportPage />
                </ProtectedLayout>
              }
            />

              {/* Activity Sessions — Admin + Support + ReleaseManager */}
              <Route
                path="/sessions/clients"
                element={
                  <ProtectedLayout allowedRoles={['FleetAdmin', 'SupportEngineer', 'ReleaseManager']}>
                    <ClientActivitySessionsPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/sessions/clients/:clientId/logs/:sessionId"
                element={
                  <ProtectedLayout allowedRoles={['FleetAdmin', 'SupportEngineer', 'ReleaseManager']}>
                    <ClientLogDetailPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/sessions/devices"
                element={
                  <ProtectedLayout allowedRoles={['FleetAdmin', 'SupportEngineer', 'ReleaseManager']}>
                    <DeviceActivitySessionsPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/sessions/devices/:deviceSerial/logs/:sessionId"
                element={
                  <ProtectedLayout allowedRoles={['FleetAdmin', 'SupportEngineer', 'ReleaseManager']}>
                    <DeviceLogDetailPage />
                  </ProtectedLayout>
                }
              />
              {/* Fleet-wide session analytics. FleetAdmin ONLY — narrower than the
                  three routes above, and it must stay in step with role_matrix
                  ('GET /analytics/sessions/summary': ADMIN). If the two drift, the
                  portal and the service disagree about who may see this. */}
              <Route
                path="/sessions/analytics"
                element={
                  <ProtectedLayout allowedRoles={['FleetAdmin']}>
                    <SessionAnalyticsPage />
                  </ProtectedLayout>
                }
              />

              {/* Admin — Admin only (except the audit log: Fleet Admin + Release Manager, FMP-AT-001) */}
              <Route
                path="/admin/users"
                element={
                  <ProtectedLayout allowedRoles={['FleetAdmin']}>
                    <UserManagementPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/admin/notifications"
                element={
                  <ProtectedLayout allowedRoles={['FleetAdmin']}>
                    <SendNotificationPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/admin/segments"
                element={
                  <ProtectedLayout allowedRoles={['FleetAdmin']}>
                    <SegmentsPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/admin/audit"
                element={
                  <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager']}>
                    <AuditLogPage />
                  </ProtectedLayout>
                }
              />
              {/* Security events (refused requests): Fleet Admin + Support Engineer.
                  Not the audit log's roles - that is FMP-AT-001's own set. */}
              <Route
                path="/admin/unusual-activity"
                element={
                  <ProtectedLayout allowedRoles={['FleetAdmin', 'SupportEngineer']}>
                    <UnusualActivityPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/admin/device-catalog"
                element={
                  <ProtectedLayout allowedRoles={['FleetAdmin']}>
                    <DeviceCatalogPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/admin/config"
                element={
                  <ProtectedLayout allowedRoles={['FleetAdmin']}>
                    <SystemConfigPage />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/admin/system-health"
                element={
                  <ProtectedLayout allowedRoles={['FleetAdmin', 'SupportEngineer']}>
                    <SystemHealthPage />
                  </ProtectedLayout>
                }
              />

              {/* INC 2 stub — all web roles can preview */}
              <Route
                path="/inc2/logs"
                element={
                  <ProtectedLayout allowedRoles={['FleetAdmin', 'ReleaseManager', 'SupportEngineer']}>
                    <LogTriagePage />
                  </ProtectedLayout>
                }
              />

              {/* Catch-all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </NotificationProvider>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

